import { JOURNEE_PAR_DEFAUT } from '@nextilt/commun';
import type { CourageDeMaintenant, Ou } from '../../partage/api/types';
import { desHeure, MAINTENANT_DEBUT, MOTS_COURAGE, MOTS_OU_DU_JOUR, MOTS_TEMPS_DE_LA_QUESTION } from '../../partage/vocabulaire';
import { dureeLisible, heureLisible } from './datesDeLAgenda';
import type { GesteDuJour } from '../../agenda';

/**
 * Les règles sans rendu de la question avant de planifier un jour, au téléphone (chantier
 * `telephone-journee-agenda`, ticket 01, décisions d'Igor du 2026-10-05, 7, 8, 9 et 20) :
 * quand elle se pose, ce qu'elle propose, ses réponses par défaut, et leur résumé.
 */

/** Ce qu'on répond à la question : combien de temps, quel courage, dès quelle heure, où. */
export interface Reponses {
  tempsMin: number;
  courage: CourageDeMaintenant;
  debut: string;
  ou: Ou;
}

/** Les temps proposés : 1 h, 2 h, ½ journée, journée (décision 20). */
export const TEMPS_DE_LA_QUESTION: readonly number[] = MOTS_TEMPS_DE_LA_QUESTION.map(([v]) => v);
/** Les heures de début toutes prêtes, celles du panneau « Préparer ma journée ». */
export const DEBUTS_DE_LA_QUESTION: readonly string[] = ['08:00', '09:00', '14:00', '18:00'];

/**
 * Les réponses par défaut, celles d'une journée par défaut (`JOURNEE_PAR_DEFAUT`, les mêmes que
 * le panneau de l'ordinateur et le +) : 2 h, Normal, « Chez moi ou dehors », et dès maintenant
 * arrondi pour aujourd'hui (le serveur le dit), sinon 9 h.
 */
export function reponsesParDefaut(maintenant: string | null | undefined): Reponses {
  const { tempsMin, courage, debut, ou } = JOURNEE_PAR_DEFAUT;
  return { tempsMin, courage, debut: maintenant ?? debut, ou };
}

/** Les réponses retenues : celles qu'on a touchées, les autres par défaut. */
export function reponsesRetenues(touchees: Partial<Reponses>, maintenant: string | null | undefined): Reponses {
  return { ...reponsesParDefaut(maintenant), ...touchees };
}

/**
 * Les heures de début proposées : aujourd'hui, « Maintenant » puis celles qui ne sont pas encore
 * passées ; un autre jour, toutes.
 */
export function debutsProposes(maintenant: string | null | undefined): string[] {
  if (!maintenant) return [...DEBUTS_DE_LA_QUESTION];
  return [maintenant, ...DEBUTS_DE_LA_QUESTION.filter((d) => d > maintenant)];
}

/**
 * La question se pose quand rien n'est prévu ce jour-là : pas de journée, ou une journée sans
 * tâche. Une seule tâche posée ce jour-là en fait une journée prévue (décision 9 du cadrage).
 */
export function poseLaQuestion(journee: { taches: readonly unknown[] } | null | undefined): boolean {
  return !journee || journee.taches.length === 0;
}

/**
 * Le geste d'un jour de l'Agenda qui pose la question : « Planifier ce jour », sur un jour à
 * venir ou aujourd'hui, sans journée. Changer une journée déjà là rouvre le panneau, et un jour
 * passé n'a pas de geste (`gesteDuJour`).
 */
export function ouvreLaQuestion(geste: GesteDuJour): boolean {
  return geste === 'preparer' || geste === 'planifier';
}

/** Le mot d'un temps de la question (« ½ journée »), sinon la durée lisible. */
export function motDuTemps(tempsMin: number): string {
  return MOTS_TEMPS_DE_LA_QUESTION.find(([v]) => v === tempsMin)?.[1] ?? dureeLisible(tempsMin);
}

/** « Dès 9 h 30 », ou « Dès maintenant » quand le début est l'heure qu'il est. */
export function motDuDebut(debut: string, maintenant: string | null | undefined): string {
  return desHeure(debut === maintenant ? MAINTENANT_DEBUT.toLocaleLowerCase('fr-FR') : heureLisible(debut));
}

/** « 2 h · dès 9 h 30 · chez moi · Normal », comme la maquette, sous le titre de l'écran suivant. */
export function resumeDesReponses(r: Reponses, maintenant: string | null | undefined): string {
  return [
    motDuTemps(r.tempsMin),
    motDuDebut(r.debut, maintenant).toLocaleLowerCase('fr-FR'),
    MOTS_OU_DU_JOUR[r.ou].toLocaleLowerCase('fr-FR'),
    MOTS_COURAGE[r.courage],
  ].join(' · ');
}
